import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import {
  TransformComponent,
  TransformWrapper,
  type ReactZoomPanPinchRef,
} from "react-zoom-pan-pinch";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { OfflineImage } from "@/components/common/offline-image/offline-image";
import type { FilmstripDrag } from "./use-filmstrip-drag";

/** One slide on the stage. */
export interface FilmstripPhoto {
  id: string;
  /** Offline-cache key (content-versioned). Empty string disables cache lookup. */
  assetKey: string;
  /** Live image URL — used online and as the cache fallback. */
  src: string;
  alt?: string;
}

/** The active slide's imperative zoom, for a zoom button or a reset. */
export interface SlideZoomControls {
  reset: () => void;
  zoomIn: () => void;
}

// Double-tap / zoom-button target. react-zoom-pan-pinch's doubleClick step is
// additive from the current scale; 1.4 from the 1.0 base lands at the design's
// 2.4x zoom.
const ZOOM_STEP = 1.4;

/**
 * The zoom/pan engine for one slide — the same `react-zoom-pan-pinch`
 * configuration ExpandedImageView uses (initialScale 1, min 0.5 / max 5, wheel
 * + pinch), with double-tap toggle zoom layered on per the design. Reused here
 * so pinch / scroll / double-tap zoom + pan behave identically inside the
 * carousel. The active slide registers its imperative `reset` + `zoomIn` with
 * the parent so the dedicated zoom button and slide-change cleanup can drive
 * it. See [[expanded-image-view.spec]].
 */
function ZoomableSlide({
  photo,
  active,
  enablePanning,
  onZoomChange,
  registerControls,
}: {
  photo: FilmstripPhoto;
  active: boolean;
  /** Pan is the zoom engine's only when this slide is zoomed; otherwise the
   *  carousel owns horizontal drag, so panning stays disabled. */
  enablePanning: boolean;
  onZoomChange?: (zoomed: boolean) => void;
  registerControls?: (controls: SlideZoomControls | null) => void;
}) {
  const ref = useRef<ReactZoomPanPinchRef>(null);

  useEffect(() => {
    if (!active || !registerControls) return;
    registerControls({
      reset: () => ref.current?.resetTransform(),
      zoomIn: () => ref.current?.zoomIn(ZOOM_STEP),
    });
    return () => registerControls(null);
  }, [active, registerControls]);

  return (
    <TransformWrapper
      ref={ref}
      initialScale={1}
      minScale={0.5}
      maxScale={5}
      centerOnInit
      wheel={{ step: 0.002 }}
      panning={{ velocityDisabled: true, disabled: !enablePanning }}
      pinch={{ step: 5 }}
      doubleClick={{ mode: "toggle", step: ZOOM_STEP, animationTime: 250 }}
      onTransform={(r) => {
        if (active) onZoomChange?.(r.state.scale > 1.01);
      }}
    >
      <TransformComponent
        wrapperStyle={{ width: "100%", height: "100%" }}
        contentStyle={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="flex h-full w-full items-center justify-center"
          style={{
            touchAction: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
            WebkitTouchCallout: "none",
          }}
        >
          <OfflineImage
            assetKey={photo.assetKey}
            src={photo.src}
            alt={photo.alt ?? ""}
            draggable={false}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      </TransformComponent>
    </TransformWrapper>
  );
}

interface FilmstripStageProps {
  photos: FilmstripPhoto[];
  /** The carousel state from `useFilmstripDrag`, created against `stageRef`. */
  film: FilmstripDrag;
  stageRef: RefObject<HTMLDivElement | null>;
  zoomed: boolean;
  onZoomChange: (zoomed: boolean) => void;
  registerControls: (controls: SlideZoomControls | null) => void;
  /** Overlays drawn over the stage (a hint, a banner). */
  children?: ReactNode;
}

/**
 * The swipeable stage both full-screen photo viewers share: the slide track
 * driven by `useFilmstripDrag`, a zoomable slide for the current photo and its
 * neighbours only, and the prev/next chevrons (hidden for one photo, disabled
 * at the ends). The viewer around it owns the top bar, the thumbnail strip and
 * the keyboard. See [[record-photo-viewer.spec]] and [[issue-photo-viewer.spec]].
 */
export function FilmstripStage({
  photos,
  film,
  stageRef,
  zoomed,
  onZoomChange,
  registerControls,
  children,
}: FilmstripStageProps) {
  const { index } = film;
  const count = photos.length;
  const multi = count > 1;

  // Windowed render — only current ±1 slides are mounted.
  const lo = Math.max(0, index - 1);
  const hi = Math.min(count - 1, index + 1);
  const windowIndices: number[] = [];
  for (let i = lo; i <= hi; i++) windowIndices.push(i);

  return (
    <div
      ref={stageRef}
      className="relative min-h-0 flex-1 overflow-hidden"
      onPointerDown={film.onPointerDown}
      onPointerMove={film.onPointerMove}
      onPointerUp={film.onPointerUp}
      onPointerCancel={film.onPointerCancel}
    >
      {/* Track — translated in slide units; no CSS transition (rAF drives it). */}
      <div
        className="absolute inset-0"
        style={{ transform: `translateX(${film.trackFraction * 100}%)` }}
      >
        {windowIndices.map((i) => (
          <div
            key={photos[i].id}
            className="absolute inset-0"
            style={{ transform: `translateX(${i * 100}%)` }}
          >
            <ZoomableSlide
              photo={photos[i]}
              active={i === index}
              enablePanning={i === index && zoomed}
              onZoomChange={i === index ? onZoomChange : undefined}
              registerControls={i === index ? registerControls : undefined}
            />
          </div>
        ))}
      </div>

      {/* Navigation chevrons — hidden for one photo, disabled at the ends. */}
      {multi && (
        <>
          <button
            type="button"
            onClick={() => film.step(-1)}
            disabled={!film.canPrev}
            className={cn(
              "absolute left-2.5 top-1/2 z-[8] flex h-[42px] w-[42px] -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/65",
              !film.canPrev && "pointer-events-none opacity-40",
            )}
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-[22px] w-[22px]" />
          </button>
          <button
            type="button"
            onClick={() => film.step(1)}
            disabled={!film.canNext}
            className={cn(
              "absolute right-2.5 top-1/2 z-[8] flex h-[42px] w-[42px] -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white transition-colors hover:bg-black/65",
              !film.canNext && "pointer-events-none opacity-40",
            )}
            aria-label="Next photo"
          >
            <ChevronRight className="h-[22px] w-[22px]" />
          </button>
        </>
      )}

      {children}
    </div>
  );
}
